import React from 'react';
import PropTypes from 'prop-types';
import useAddress from '@afosto/queries/contact/useAddress';
import useFulfilmentLocation from '@afosto/queries/fulfilment/useFulfilmentLocation';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useParcel from '@afosto/queries/fulfilment/useParcel';
import useShipment from '@afosto/queries/fulfilment/useShipment';
import useShippingMethod from '@afosto/queries/fulfilment/useShippingMethod';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Address from '@afosto/app-components-react/Address';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import ItemCount from '../../../components/ItemCount';
import ParcelStatusIcon from '../../../components/ParcelStatusIcon';
import ParcelsCount from './ParcelsCount';
import getShippingMethodName from '../../../utils/getShippingMethodName';
import formatDateTime from '@afosto/utils/date/formatDateTime';
import getFullName from '@afosto/utils/getFullName';
import translations from './translations';

export const ParcelCardSkeleton = () => (
  <Card>
    <CardContent>
      <SkeletonBody lines={2} />
    </CardContent>
  </Card>
);

const ParcelCard = ({ parcel, SelectionComponent }) => {
  const intl = useIntl();
  const { id } = parcel ?? {};
  const { data: parcelData } = useParcel(id);
  const { data: shipment } = useShipment(parcelData?.shipment_id, {
    enabled: !!parcelData?.shipment_id,
  });
  const { data: shippingMethod } = useShippingMethod(shipment?.method_id, {
    enabled: !!shipment?.method_id,
  });

  const addressing = shipment?.addressing ?? {};
  const destinationIsLocation = addressing.to?.entity_type === 'location';

  const toLocationId = destinationIsLocation ? addressing.to.entity_id : null;
  const { data: toLocation } = useFulfilmentLocation(toLocationId, { enabled: !!toLocationId });

  const addressId = destinationIsLocation ? toLocationId?.address_id : addressing.to?.entity_id;
  const { data: toAddress } = useAddress(addressId, { enabled: !!addressId });

  const itemCount = parcelData?.items?.length ?? 0;
  const parcelsCount = shipment?.parcels?.length ?? 0;

  return (
    <Card>
      <Box display="flex" flexWrap="nowrap" alignItems="center">
        {SelectionComponent && <SelectionComponent />}
        <Box display="flex" flexGrow={1} alignItems="center">
          <CardActionArea component={Link} to={`/parcels/${parcel.id}`}>
            <Box p={2} display="flex" alignItems="center">
              <Box flex={1}>
                <Box display="flex">
                  <Box mr="auto" flexGrow={1}>
                    <Typography variant="subtitle1">
                      {parcel.number?.toUpperCase() || parcel.id}
                    </Typography>
                    <Box mt={2} pb={0.5}>
                      <FormattedMessage {...translations.properties.shipment} />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {parcel?.shipment_number}
                    </Typography>
                    <Box mt={2} pb={0.5}>
                      <FormattedMessage {...translations.properties.shipAt} />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {parcel?.ship_at ? formatDateTime(parcel.ship_at) : '-'}
                    </Typography>
                  </Box>
                  <Box px={2} py={1}>
                    <ParcelStatusIcon parcel={parcel} />
                  </Box>
                </Box>
                {parcelData?.position && (
                  <Box mt={2}>
                    <Box pb={0.5}>
                      <FormattedMessage {...translations.properties.position} />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {parcelData.position}
                    </Typography>
                  </Box>
                )}
                {shippingMethod && (
                  <Box mt={2}>
                    <Box pb={0.5}>
                      <FormattedMessage {...translations.properties.shippingMethod} />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {getShippingMethodName(intl, shippingMethod)}
                    </Typography>
                  </Box>
                )}
                {(toLocation || toAddress) && (
                  <Box mt={2}>
                    <Box pb={0.5}>
                      <FormattedMessage {...translations.properties.shippingAddress} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" component="div">
                      {toLocation && <Typography variant="body2">{toLocation.name}</Typography>}
                      {toAddress && (
                        <Address
                          company={toAddress.organisation}
                          fullName={getFullName(toAddress)}
                          addressLine1={toAddress.address_line_1}
                          addressLine2={toAddress.address_line_2}
                          postalCode={toAddress.postal_code}
                          city={toAddress.locality}
                          country={toAddress.country_code}
                          administrativeArea={toAddress.administrative_area}
                        />
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box pr={2} display="flex" justifyContent="space-between">
              <Box px={2} pb={2}>
                <ItemCount itemCount={itemCount} />
              </Box>
              <ParcelsCount parcelsCount={parcelsCount} />
            </Box>
          </CardActionArea>
        </Box>
      </Box>
    </Card>
  );
};

ParcelCard.propTypes = {
  parcel: PropTypes.object.isRequired,
  SelectionComponent: PropTypes.oneOfType([
    PropTypes.element,
    // eslint-disable-next-line react/no-typos
    PropTypes.elementType,
    PropTypes.node,
    PropTypes.string,
  ]),
};

ParcelCard.defaultProps = {
  SelectionComponent: undefined,
};

export default ParcelCard;
